import { LONG_DATE_FORMAT, SHORT_DATE_FORMAT, convertsCentsToCurrency } from '@npi/buddy-utils';
import { CommissionStatementStatus, CreditNoteStatus, ProductType } from '@prisma/client';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { CreditNote, GetSalesInvoice, PaymentAllocation } from 'types/graphql';

import { PaymentAllocationStatus } from 'src/constants';
import { PAYMENT_METHODS } from 'src/constants/paymentmethods';
import { enumToCamelCase } from 'src/lib/formatters';
import { FilterOption } from 'src/types';
import { formatDate, formatProductId } from 'src/utils/utils';

export type FilterOptionsType = {
    [key: string]: FilterOption[];
};

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export type CommissionFilter = {
    id: number;
    commissionStatementId: string;
};

export const commissionFilters = (idFilters: CommissionFilter[], partnerNameFilters: string[]) => {
    return Object.fromEntries([
        [
            'ID',
            (idFilters || []).map(({ id, commissionStatementId }) => {
                return {
                    value: id,
                    label: commissionStatementId,
                };
            }),
        ],
        [
            'Status',
            Object.keys(CommissionStatementStatus).map((commissionStatus) => {
                return {
                    value: CommissionStatementStatus[`${commissionStatus}`],
                    label: CommissionStatementStatus[`${commissionStatus}`].replaceAll('_', ' '),
                };
            }),
        ],
        [
            'Partner Name',
            (partnerNameFilters || []).map((partnerNameFilter) => {
                return {
                    value: partnerNameFilter,
                    label: partnerNameFilter,
                };
            }),
        ],
        ['Date', []],
    ]) as FilterOptionsType;
};

export const userRolesFilteredTableData = (data, tableFilters) => {
    let filteredRoles = data?.roles ?? [];
    if (Object.keys(tableFilters).length > 0) {
        if (tableFilters?.userrole && tableFilters?.hierarchylevel) {
            const { userrole, hierarchylevel } = tableFilters;
            filteredRoles = filteredRoles.filter(({ name, hierarchyLevel }) => {
                return name === userrole && hierarchyLevel === hierarchylevel;
            });
        } else if (tableFilters?.userrole) {
            const { userrole } = tableFilters;
            filteredRoles = filteredRoles.filter(({ name }) => {
                return name === userrole;
            });
        } else if (tableFilters?.hierarchylevel) {
            const { hierarchylevel } = tableFilters;
            filteredRoles = filteredRoles.filter(({ hierarchyLevel }) => {
                return hierarchyLevel === hierarchylevel;
            });
        } else if (tableFilters?.search) {
            const { search } = tableFilters;
            filteredRoles = filteredRoles.filter((filteredRole) => {
                return filteredRole?.name?.toLowerCase()?.includes(search?.toLowerCase());
            });
        }
    }
    return filteredRoles.map((role) => {
        return {
            ...role,
            role: role?.name,
            hierarchyLevel: enumToCamelCase(role.hierarchyLevel),
            users: role.users?.length || 0,
            usersRecord: role.users?.length > 0 ? role.users : [],
        };
    });
};

export const usersTableFilteredData = (data, tableFilters) => {
    let filteredUsers = data?.users ?? [];
    if (Object.keys(tableFilters)?.length > 0) {
        if (tableFilters?.userrole && tableFilters?.hierarchylevel) {
            const { userrole, hierarchylevel } = tableFilters;
            filteredUsers = filteredUsers.filter(({ role }) => {
                return role?.name === userrole && role?.hierarchyLevel === hierarchylevel;
            });
        } else if (tableFilters?.userrole) {
            const { userrole } = tableFilters;
            filteredUsers = filteredUsers.filter(({ role }) => {
                return role?.name === userrole;
            });
        } else if (tableFilters?.hierarchylevel) {
            const { hierarchylevel } = tableFilters;
            filteredUsers = filteredUsers.filter(({ role }) => {
                return role?.hierarchyLevel === hierarchylevel;
            });
        } else if (tableFilters?.search) {
            const { search } = tableFilters;
            filteredUsers = filteredUsers.filter((filteredUser) => {
                return filteredUser?.username?.toLowerCase()?.includes(search?.toLowerCase());
            });
        }
    }

    return filteredUsers.map((user) => {
        const partner =
            !user.business?.hierarchyLevel || user.business?.hierarchyLevel === 'NPI'
                ? '-'
                : user.business?.legalName;
        return {
            ...user,
            role: enumToCamelCase(user.role.name),
            roleId: user.role.id,
            partner,
            hierarchyLevel: enumToCamelCase(user.role.hierarchyLevel),
        };
    });
};

export const creditsFilteredTableData = (data, tableFilters) => {
    let filteredCredits = data?.creditNotes ?? [];
    if (Object.keys(tableFilters)?.length > 0) {
        if (
            tableFilters?.creditnotenumber &&
            tableFilters?.merchantname &&
            tableFilters?.partnername &&
            tableFilters?.dateadded &&
            tableFilters?.invoicenumber
        ) {
            const { creditnotenumber, merchantname, partnername, dateadded, invoicenumber } =
                tableFilters;
            filteredCredits = filteredCredits.filter(
                ({ id, merchant, partner, createdAt, salesInvoice }) => {
                    return (
                        id === creditnotenumber &&
                        merchantname === merchant?.business?.legalName &&
                        partnername === partner?.business?.legalName &&
                        dateadded === createdAt &&
                        parseInt(invoicenumber) === salesInvoice?.id
                    );
                }
            );
        } else if (
            tableFilters?.merchantname &&
            tableFilters?.partnername &&
            tableFilters?.dateadded &&
            tableFilters?.invoicenumber
        ) {
            const { merchantname, partnername, dateadded, invoicenumber } = tableFilters;
            filteredCredits = filteredCredits.filter(
                ({ merchant, partner, createdAt, salesInvoice }) => {
                    return (
                        merchantname === merchant?.business?.legalName &&
                        partnername === partner?.business?.legalName &&
                        dateadded === createdAt &&
                        invoicenumber === `SI-${salesInvoice?.id}`
                    );
                }
            );
        } else if (
            tableFilters?.partnername &&
            tableFilters?.dateadded &&
            tableFilters?.invoicenumber
        ) {
            const { partnername, dateadded, invoicenumber } = tableFilters;
            filteredCredits = filteredCredits.filter(({ partner, createdAt, salesInvoice }) => {
                return (
                    partnername === partner?.business?.legalName &&
                    dateadded === createdAt &&
                    invoicenumber === `SI-${salesInvoice?.id}`
                );
            });
        } else if (tableFilters?.dateadded && tableFilters?.invoicenumber) {
            const { dateadded, invoicenumber } = tableFilters;
            filteredCredits = filteredCredits.filter(({ createdAt, salesInvoice }) => {
                return dateadded === createdAt && invoicenumber === `SI-${salesInvoice?.id}`;
            });
        } else if (tableFilters?.invoicenumber) {
            const { invoicenumber } = tableFilters;
            filteredCredits = filteredCredits.filter(({ salesInvoice }) => {
                return invoicenumber === `SI-${salesInvoice?.id}`;
            });
        } else if (tableFilters?.dateadded) {
            const { dateadded } = tableFilters;
            filteredCredits = filteredCredits.filter(({ createdAt }) => {
                return dateadded === createdAt;
            });
        } else if (tableFilters?.partnername) {
            const { partnername } = tableFilters;
            filteredCredits = filteredCredits.filter(({ partner }) => {
                return partnername === partner?.business?.legalName;
            });
        } else if (tableFilters?.merchantname) {
            const { merchantname } = tableFilters;
            filteredCredits = filteredCredits.filter(({ merchant }) => {
                return merchantname === merchant?.business?.legalName;
            });
        } else if (tableFilters?.creditnotenumber) {
            const { creditnotenumber } = tableFilters;
            filteredCredits = filteredCredits.filter(({ id }) => {
                return id === creditnotenumber;
            });
        } else if (tableFilters?.search) {
            const { search } = tableFilters;
            filteredCredits = filteredCredits.filter((filteredCredit) => {
                return filteredCredit?.partner?.business?.legalName
                    ?.toLowerCase()
                    ?.includes(search?.toLowerCase());
            });
        }
    }

    return filteredCredits.map((creditNote) => {
        return {
            ...creditNote,
            id: creditNote.id,
            salesInvoiceId: `SI-${creditNote?.salesInvoice?.id}`,
        };
    });
};

export const creditNotesFilterOptions = (data) => {
    const credits = data?.creditNotes ?? [];
    return Object.fromEntries([
        [
            'Credit Note Number',
            credits.map(({ id }) => {
                return { value: id, label: id };
            }),
        ],
        [
            'Merchant Name',
            Array.from(
                new Set(
                    credits.map((credit) => {
                        return credit?.merchant?.business?.legalName;
                    })
                )
            )
                .filter((merchantName) => {
                    return merchantName;
                })
                .map((merchantName) => {
                    return { value: merchantName, label: merchantName };
                }),
        ],
        [
            'Partner Name',
            Array.from(
                new Set(
                    credits.map((credit) => {
                        return credit?.partner?.business?.legalName;
                    })
                )
            )
                .filter((partnerName) => {
                    return partnerName;
                })
                .map((partnerName) => {
                    return { value: partnerName, label: partnerName };
                }),
        ],
        [
            'Date Added',
            Array.from(
                new Set(
                    credits.map(({ createdAt }) => {
                        return formatDate({ date: new Date(createdAt) });
                    })
                )
            )
                .filter((createdDate) => {
                    return createdDate;
                })
                .map((createdDate) => {
                    return {
                        value: createdDate,
                        label: createdDate,
                    };
                }),
        ],
        [
            'Invoice Number',
            Array.from(
                credits.map((credit) => {
                    const salesInvoice = credit?.salesInvoice;
                    return `SI-${salesInvoice?.id}`;
                })
            )
                .filter((invoiceId) => {
                    return invoiceId;
                })
                .map((invoiceId) => {
                    return {
                        value: `SI-${invoiceId}`,
                        label: `SI-${invoiceId}`,
                    };
                }),
        ],
    ]) as FilterOptionsType;
};

export const filteredProductsTableData = (data, tableFilters) => {
    let filteredProducts = data?.products ?? [];
    if (Object.keys(tableFilters)?.length > 0) {
        if (
            tableFilters?.productname &&
            tableFilters?.producttype &&
            tableFilters['primary/secondary'] &&
            tableFilters?.status
        ) {
            const { productname, producttype, status: statusFilter } = tableFilters;
            filteredProducts = filteredProducts.filter(
                ({ name, type: productType, catalogue, status }) => {
                    return (
                        productname === name &&
                        producttype === productType &&
                        tableFilters['primary/secondary'] === catalogue &&
                        statusFilter === status
                    );
                }
            );
        } else if (
            tableFilters?.producttype &&
            tableFilters['primary/secondary'] &&
            tableFilters?.status
        ) {
            const { producttype, status: statusFilter } = tableFilters;
            filteredProducts = filteredProducts.filter(
                ({ type: productType, catalogue, status }) => {
                    return (
                        producttype === productType &&
                        tableFilters['primary/secondary'] === catalogue &&
                        statusFilter === status
                    );
                }
            );
        } else if (tableFilters['primary/secondary'] && tableFilters?.status) {
            const { status: statusFilter } = tableFilters;
            filteredProducts = filteredProducts.filter(({ catalogue, status }) => {
                return tableFilters['primary/secondary'] === catalogue && statusFilter === status;
            });
        } else if (tableFilters?.status) {
            const { status: statusFilter } = tableFilters;
            filteredProducts = filteredProducts.filter(({ status }) => {
                return statusFilter === status;
            });
        } else if (tableFilters['primary/secondary']) {
            filteredProducts = filteredProducts.filter(({ catalogue }) => {
                return tableFilters['primary/secondary'] === catalogue;
            });
        } else if (tableFilters?.producttype) {
            const { producttype } = tableFilters;
            filteredProducts = filteredProducts.filter(({ type: productType }) => {
                return producttype === productType;
            });
        } else if (tableFilters?.productname) {
            const { productname } = tableFilters;
            filteredProducts = filteredProducts.filter(({ name }) => {
                return productname === name;
            });
        } else if (tableFilters?.search) {
            const { search } = tableFilters;
            filteredProducts = filteredProducts.filter(({ name }) => {
                return name?.toLowerCase()?.includes(search?.toLowerCase());
            });
        }
    }

    return filteredProducts.map((product) => {
        const id = product.type === ProductType.ONLINE ? product.onlineId : product.terminalId;

        return {
            ...product,
            id: product.id,
            productId: formatProductId(product.type, id),
            status: product.status,
        };
    });
};

export const filteredPricingOffersTableData = (offerData, tableFilters) => {
    let filteredPricingOffers = offerData;
    if (Object.keys(tableFilters).length > 0) {
        if (tableFilters?.partnername && tableFilters?.status) {
            const { partnername, status: offerStatus } = tableFilters;
            filteredPricingOffers = filteredPricingOffers.filter((terminalOffer) => {
                const legalName = terminalOffer.partners.find((partner) => {
                    return partner?.business?.legalName?.length > 0;
                })?.business?.legalName;

                return (
                    (legalName === partnername || terminalOffer?.name === partnername) &&
                    terminalOffer?.status === offerStatus
                );
            });
        } else if (tableFilters?.partnername) {
            const { partnername } = tableFilters;
            filteredPricingOffers = filteredPricingOffers.filter((terminalOffer) => {
                const legalName = terminalOffer.partners.find((partner) => {
                    return partner?.business?.legalName?.length > 0;
                })?.business?.legalName;
                return legalName === partnername || terminalOffer?.name === partnername;
            });
        } else if (tableFilters?.status) {
            const { status: offerStatus } = tableFilters;
            filteredPricingOffers = filteredPricingOffers.filter(({ status }) => {
                return status === offerStatus;
            });
        } else if (tableFilters?.search) {
            const { search } = tableFilters;
            filteredPricingOffers = filteredPricingOffers.filter((terminalOffer) => {
                const legalName = terminalOffer.partners.find((partner) => {
                    return partner?.business?.legalName?.length > 0;
                })?.business?.legalName;
                return (
                    terminalOffer?.name?.toLowerCase()?.includes(search.toLowerCase()) ||
                    legalName?.toLowerCase()?.includes(search.toLowerCase())
                );
            });
        }
    }

    return filteredPricingOffers.map((terminalOffer) => {
        const partnerName = terminalOffer.partners.find((partner) => {
            return partner?.business?.legalName?.length > 0;
        })?.business?.legalName;
        return {
            ...terminalOffer,
            name: terminalOffer?.name,
            partnerName,
        };
    });
};

export const partnersFilterOptions = (partnersData) => {
    return Object.fromEntries([
        [
            'Name',
            Array.from(
                new Set(
                    partnersData.map(({ name }) => {
                        return name;
                    })
                )
            )
                .map((partnerName) => {
                    return { value: partnerName, label: partnerName };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
        [
            'Partner Type',
            Array.from(
                new Set(
                    partnersData.map(({ type: partnerType }) => {
                        return partnerType;
                    })
                )
            )
                .map((partnerType) => {
                    return { value: partnerType, label: partnerType };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
        [
            'Primary Contact Name',
            Array.from(
                new Set(
                    partnersData.map(({ primaryContactName }) => {
                        return primaryContactName;
                    })
                )
            )
                .map((primaryContactName) => {
                    return {
                        value: primaryContactName,
                        label: primaryContactName,
                    };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
        [
            'Status',
            Array.from(
                new Set(
                    partnersData.map(({ status }) => {
                        return status;
                    })
                )
            )
                .map((status) => {
                    return {
                        value: status,
                        label: status,
                    };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
    ]) as FilterOptionsType;
};

export const managePartnerResourcesFilterOptions = (resources) => {
    return Object.fromEntries([
        [
            'Resource Name',
            Array.from(
                new Set(
                    resources.map(({ name }) => {
                        return name;
                    })
                )
            )
                .map((resourceName) => {
                    return { value: resourceName, label: resourceName };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
        [
            'Section Name',
            Array.from(
                new Set(
                    resources.map(({ section: sectionName }) => {
                        return sectionName?.name;
                    })
                )
            )
                .map((section) => {
                    return { value: section, label: section };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
        [
            'Category',
            Array.from(
                new Set(
                    resources.map(({ type }) => {
                        return type;
                    })
                )
            )
                .map((category) => {
                    return {
                        value: category,
                        label: category,
                    };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
        [
            'Status',
            Array.from(
                new Set(
                    resources.map(({ status }) => {
                        return status;
                    })
                )
            )
                .map((status) => {
                    return {
                        value: status,
                        label: status,
                    };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
    ]) as FilterOptionsType;
};

export enum SEPA_MANDATE_AGREEMENT_TABLE_TYPE {
    MERCHANT = 'MERCHANT',
    PARTNER = 'PARTNER',
}

export const sepaMandateAgreementFilterOptions = (
    sepaMandateAgreementFilterOptionsData,
    entity: SEPA_MANDATE_AGREEMENT_TABLE_TYPE
): FilterOptionsType => {
    return Object.fromEntries([
        [
            entity === 'MERCHANT' ? 'Merchant' : 'Partner',
            Array.from(
                new Set(
                    sepaMandateAgreementFilterOptionsData?.map((agreement) => {
                        return agreement?.tradingName;
                    })
                )
            )
                .map((tradingName) => {
                    return { value: tradingName, label: tradingName };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
        [
            'Account Number',
            Array.from(
                new Set(
                    sepaMandateAgreementFilterOptionsData?.map((agreement) => {
                        return agreement?.accountNumber;
                    })
                )
            )
                .map((accountNumber) => {
                    return {
                        value: accountNumber,
                        label: accountNumber,
                    };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
        [
            'Status',
            Array.from(
                new Set(
                    sepaMandateAgreementFilterOptionsData?.map((agreement) => {
                        return agreement?.status;
                    })
                )
            )
                .map((status) => {
                    return { value: status, label: status };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
        [
            'Contact Name',
            Array.from(
                new Set(
                    sepaMandateAgreementFilterOptionsData?.map((agreement) => {
                        return agreement?.contactName;
                    })
                )
            )
                .map((contactName) => {
                    return { value: contactName, label: contactName };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
        [
            'Sent To',
            Array.from(
                new Set(
                    sepaMandateAgreementFilterOptionsData?.map((agreement) => {
                        return agreement?.sentToEmail;
                    })
                )
            )
                .map((sentToEmail) => {
                    return { value: sentToEmail, label: sentToEmail };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
        [
            'Added By',
            Array.from(
                new Set(
                    sepaMandateAgreementFilterOptionsData?.map((agreement) => {
                        return agreement?.addedByName;
                    })
                )
            )
                .map((addedByName) => {
                    return { value: addedByName, label: addedByName };
                })
                .filter(({ value, label }) => {
                    return value && label;
                }),
        ],
    ]) as FilterOptionsType;
};

export const filteredPartnersTableData = (partnersData, tableFilters) => {
    let filteredPartners = partnersData;
    if (Object.keys(tableFilters).length > 0) {
        if (
            tableFilters?.name &&
            tableFilters?.partnertype &&
            tableFilters?.primarycontactname &&
            tableFilters?.status
        ) {
            const { name, partnertype, primarycontactname, status } = tableFilters;
            filteredPartners = filteredPartners.filter((filteredPartners) => {
                return (
                    filteredPartners?.name === name &&
                    filteredPartners?.partnerType === partnertype &&
                    filteredPartners?.primaryContactName === primarycontactname &&
                    filteredPartners?.status === status
                );
            });
        } else if (
            tableFilters?.partnertype &&
            tableFilters?.primarycontactname &&
            tableFilters?.status
        ) {
            const { partnertype, primarycontactname, status } = tableFilters;
            filteredPartners = filteredPartners.filter((filteredPartners) => {
                return (
                    filteredPartners?.partnerType === partnertype &&
                    filteredPartners?.primaryContactName === primarycontactname &&
                    filteredPartners?.status === status
                );
            });
        } else if (tableFilters?.primarycontactname && tableFilters?.status) {
            const { primarycontactname, status } = tableFilters;
            filteredPartners = filteredPartners.filter((filteredPartners) => {
                return (
                    filteredPartners?.primaryContactName === primarycontactname &&
                    filteredPartners?.status === status
                );
            });
        } else if (tableFilters?.status) {
            const { status } = tableFilters;
            filteredPartners = filteredPartners.filter((filteredPartners) => {
                return filteredPartners?.status === status;
            });
        } else if (tableFilters?.name) {
            const { name } = tableFilters;
            filteredPartners = filteredPartners.filter((filteredPartners) => {
                return filteredPartners?.name === name;
            });
        } else if (tableFilters?.partnertype) {
            const { partnertype } = tableFilters;
            filteredPartners = filteredPartners.filter((filteredPartners) => {
                return filteredPartners?.type === partnertype;
            });
        } else if (tableFilters?.primarycontactname) {
            const { primarycontactname } = tableFilters;
            filteredPartners = filteredPartners.filter((filteredPartners) => {
                return filteredPartners?.primaryContactName === primarycontactname;
            });
        } else if (tableFilters?.search) {
            const { search } = tableFilters;
            filteredPartners = filteredPartners.filter((filteredPartner) => {
                return filteredPartner?.primaryContactName
                    ?.toLowerCase()
                    ?.includes(search.toLowerCase());
            });
        }
    }
    return filteredPartners;
};

export const filteredMccTableData = (mccData, tableFilters) => {
    let filteredMCCs = mccData?.data ?? [];

    if (tableFilters?.mccnumber) {
        filteredMCCs = filteredMCCs.filter((filteredMCC) => {
            return filteredMCC?.code === tableFilters.mccnumber;
        });
    }
    if (tableFilters?.mccfriendlyname) {
        filteredMCCs = filteredMCCs.filter((filteredMCC) => {
            return filteredMCC?.name === tableFilters.mccfriendlyname;
        });
    }
    if (tableFilters?.riskclassification) {
        filteredMCCs = filteredMCCs.filter((filteredMCC) => {
            return (
                filteredMCC?.risk &&
                filteredMCC.risk.toString().toLowerCase().trim() ===
                    tableFilters.riskclassification.toLowerCase().trim()
            );
        });
    }
    if (tableFilters?.search) {
        const searchText = tableFilters.search.toLowerCase();
        filteredMCCs = filteredMCCs.filter((filteredMCC) => {
            const codeStr = filteredMCC?.code ? filteredMCC.code.toString().toLowerCase() : '';
            const descriptionStr = filteredMCC?.description
                ? filteredMCC.description.toLowerCase()
                : '';
            const nameStr = filteredMCC?.name ? filteredMCC.name.toLowerCase() : '';
            return (
                codeStr.includes(searchText) ||
                descriptionStr.includes(searchText) ||
                nameStr.includes(searchText)
            );
        });
    }
    return filteredMCCs;
};

export const filteredSepaMandateTableData = (sepaMandateData, tableFilters) => {
    let filteredData = sepaMandateData ?? [];
    if (Object.keys(tableFilters).length > 0) {
        // TODO: add filtering for contact name, sentToEmail, addedByName
        if (
            (tableFilters?.merchant || tableFilters?.partner) &&
            tableFilters?.accountnumber &&
            tableFilters?.status &&
            tableFilters?.search
        ) {
            const { tradingname, accountnumber, status } = tableFilters;
            filteredData = filteredData.filter((filtered) => {
                return (
                    filtered?.tradingName === tradingname &&
                    filtered?.accountNumber === accountnumber &&
                    filtered?.status === status
                );
            });
        } else if (tableFilters?.accountnumber && tableFilters?.status) {
            const { accountnumber, status } = tableFilters;
            filteredData = filteredData.filter((filtered) => {
                return filtered?.accountNumber === accountnumber && filtered?.status === status;
            });
        } else if (tableFilters?.accountnumber) {
            const { accountnumber } = tableFilters;
            filteredData = filteredData.filter((filtered) => {
                return filtered?.accountNumber === accountnumber;
            });
        } else if (tableFilters?.status) {
            const { status } = tableFilters;
            filteredData = filteredData.filter((filtered) => {
                return filtered?.status === status;
            });
        } else if (tableFilters?.merchant) {
            const { merchant } = tableFilters;
            filteredData = filteredData.filter((filtered) => {
                return filtered?.tradingName === merchant;
            });
        } else if (tableFilters?.partner) {
            const { partner } = tableFilters;
            filteredData = filteredData.filter((filtered) => {
                return filtered?.tradingName === partner;
            });
        } else if (tableFilters?.search) {
            const { search } = tableFilters;
            filteredData = filteredData.filter((filtered) => {
                return (
                    filtered?.accountNumber?.toLowerCase()?.includes(search.toLowerCase()) ||
                    filtered?.addedByName?.toLowerCase()?.includes(search.toLowerCase()) ||
                    filtered?.contactName?.toLowerCase()?.includes(search.toLowerCase()) ||
                    filtered?.sentToEmail?.toLowerCase()?.includes(search.toLowerCase()) ||
                    filtered?.tradingName?.toLowerCase()?.includes(search.toLowerCase())
                );
            });
        }
    }
    return filteredData;
};

export const filteredMerchantsTableData = (merchants, tableFilters) => {
    if (!merchants || !tableFilters || Object.keys(tableFilters).length === 0) {
        return merchants ?? [];
    }

    return merchants.filter((merchant) => {
        return Object.entries(tableFilters).every(([key, value]) => {
            if (!value) return true;

            switch (key) {
                case 'mid(merchantid)':
                    return merchant.mid === value;
                case 'applicationid':
                    return String(merchant.applicationId) === String(value);
                case 'tradingname':
                    return merchant.tradingName === value;
                case 'partnername':
                    return merchant.partnerName === value;
                case 'toplevelpartner':
                    return merchant.topLevelPartner === value;
                case 'merchantaccountnumber':
                    return merchant.merchantAccountNumber === value;
                case 'merchantcontactname':
                    return merchant.merchantContactName === value;
                case 'search': {
                    const search = (value as string).toLowerCase();
                    return (
                        merchant.topLevelPartner?.toLowerCase()?.includes(search) ||
                        merchant.tradingName?.toLowerCase()?.includes(search) ||
                        merchant.partnerName?.toLowerCase()?.includes(search) ||
                        merchant.mid?.toLowerCase()?.includes(search) ||
                        merchant.merchantAccountNumber?.toLowerCase()?.includes(search) ||
                        merchant.merchantContactName?.toLowerCase()?.includes(search) ||
                        merchant.applicationId?.toString().includes(search.toLowerCase())
                    );
                }
                default:
                    return true;
            }
        });
    });
};

export const filteredApplicationsTableData = (applicationsData, tableFilters) => {
    const filteredApplications = (
        applicationsData?.retrieveMerchantApplications?.data ?? []
    ).filter((application) => {
        const { partnername, merchantname, status, worldpaypo, search } = tableFilters;
        const date = /^\d{13}$/.test(application?.dateSubmitted)
            ? parseInt(application?.dateSubmitted)
            : application?.dateSubmitted;
        const formattedDate = formatDate({ date: new Date(date), format: SHORT_DATE_FORMAT });
        return (
            (!partnername || application?.partnerName === partnername) &&
            (!merchantname || application?.merchantName === merchantname) &&
            (!status || application?.status === status) &&
            (!worldpaypo || application?.worldpayPO === worldpaypo) &&
            (!search ||
                application?.partnerName?.toLowerCase()?.includes(search.toLowerCase()) ||
                application?.merchantName?.toLowerCase()?.includes(search.toLowerCase()) ||
                application?.worldpayPO?.toLowerCase()?.includes(search.toLowerCase()) ||
                application?.sellersName.toLowerCase().includes(search.toLowerCase()) ||
                application?.id?.toString().includes(search.toLowerCase()) ||
                formattedDate?.includes(search.toLowerCase()) ||
                application?.serviceType?.toLowerCase().includes(search.toLowerCase()) ||
                application?.status.toLowerCase().includes(search.toLowerCase()))
        );
    });

    return filteredApplications.map((application) => {
        return {
            ...application,
            partnerName: application?.partnerName,
            serviceType: application?.serviceType,
            merchantName: application?.merchantName,
            sellersName:
                application?.sellersName && application?.sellersName !== 'null null'
                    ? application?.sellersName
                    : '-',
            dateSubmitted: application?.dateSubmitted,
            worldpayPO: application?.worldpayPO,
        };
    });
};

export const updateTableFilters = (prevFilters, newFilterValue, filter) => {
    if (!newFilterValue) {
        delete prevFilters[`${filter.toLowerCase().replaceAll(' ', '')}`];
        return { ...prevFilters };
    } else if (filter === 'clear') {
        return {
            search: prevFilters?.search,
        };
    }
    return {
        ...prevFilters,
        [`${filter.toLowerCase().replaceAll(' ', '')}`]: newFilterValue,
    };
};

export const partnerResourcesFilterOptions = (data) => {
    if (!data || !data.getPartnerResources) return {};

    const resources = data.getPartnerResources ?? [];

    return Object.fromEntries([
        [
            'Section',
            Array.from(
                new Set(
                    resources.map(({ name }) => {
                        return name;
                    })
                )
            )
                .filter(Boolean)
                .map((sectionName) => {
                    return { value: sectionName, label: sectionName };
                }),
        ],
        [
            'Resource Name',
            Array.from(
                new Set(
                    resources.flatMap((section) => {
                        return section.resources.map(({ name }) => {
                            return name;
                        });
                    })
                )
            )
                .filter(Boolean)
                .map((resourceName) => {
                    return { value: resourceName, label: resourceName };
                }),
        ],
    ]) as FilterOptionsType;
};

export const filteredPartnerResourcesTableData = (data, tableFilters) => {
    if (!data || !data.getPartnerResources) return [];

    let filteredResources = data.getPartnerResources.flatMap((section) => {
        return section.resources.map((resource) => {
            return {
                ...resource,
                sectionName: section.name,
            };
        });
    });

    if (Object.keys(tableFilters).length > 0) {
        if (tableFilters?.section && tableFilters?.resourcename) {
            const { section, resourcename } = tableFilters;
            filteredResources = filteredResources.filter((resource) => {
                return resource.sectionName === section && resource.name === resourcename;
            });
        } else if (tableFilters?.section) {
            const { section } = tableFilters;
            filteredResources = filteredResources.filter((resource) => {
                return resource.sectionName === section;
            });
        } else if (tableFilters?.resourcename) {
            const { resourcename } = tableFilters;
            filteredResources = filteredResources.filter((resource) => {
                return resource.name === resourcename;
            });
        } else if (tableFilters?.search) {
            const { search } = tableFilters;
            filteredResources = filteredResources.filter((resource) => {
                return (
                    resource.name.toLowerCase().includes(search.toLowerCase()) ||
                    resource.sectionName.toLowerCase().includes(search.toLowerCase())
                );
            });
        }
    }

    return filteredResources.map((resource) => {
        return {
            ...resource,
            type: resource.type,
            status: resource.status,
        };
    });
};

export const filteredManageResourceTableData = (resources, tableFilters) => {
    return (
        resources?.filter((resource) => {
            return Object.entries(tableFilters).every(([key, value]) => {
                if (!value) return true;

                switch (key) {
                    case 'resourcename':
                        return resource?.name === value;
                    case 'category':
                        return resource?.type === value;
                    case 'sectionname':
                        return resource?.section?.name === value;
                    case 'status':
                        return resource?.status === value;
                    case 'search':
                        return resource?.name
                            ?.toLowerCase()
                            ?.includes((value as string).toLowerCase());
                    default:
                        return true;
                }
            });
        }) ?? []
    );
};

export const filteredSalesInvoicesTableData = (invoices, tableFilters) => {
    return invoices.filter((invoice) => {
        return Object.entries(tableFilters).every(([key, value]) => {
            if (!value) return true;

            switch (key) {
                case 'merchantname':
                    return invoice?.merchant?.displayName === value;
                case 'status':
                    return invoice?.status === value;
                case 'date': {
                    const createdAt = dayjs(invoice?.createdAt).startOf('day');
                    const dateRange = value as { from: Dayjs; to: Dayjs };
                    return (
                        createdAt.isSameOrAfter(dateRange.from) &&
                        createdAt.isSameOrBefore(dateRange.to)
                    );
                }
                case 'invoicenumber':
                    return invoice?.invoiceNumber === value;
                case 'merchantaccountnumber':
                    return invoice?.merchant?.merchantAccountNumber === value;
                case 'search': {
                    const search = (value as string).toLowerCase();
                    return (
                        invoice?.id.toString().includes(search) ||
                        invoice?.invoiceNumber?.toLowerCase().includes(search) ||
                        invoice?.merchant?.displayName?.toLowerCase().includes(search) ||
                        invoice?.status.toLowerCase().includes(search) ||
                        invoice?.merchant?.merchantAccountNumber?.toLowerCase().includes(search)
                    );
                }
                default:
                    return true;
            }
        });
    });
};

export const filteredAuditLogsTableData = (auditLogs, tableFilters) => {
    const { user, eventtype, date, hierarchylevel, search } = tableFilters;

    return (auditLogs ?? []).filter((log) => {
        const fullName = `${log?.user?.firstName ?? ''} ${log?.user?.lastName ?? ''}`
            .toLowerCase()
            .trim();
        const hierarchy = log?.user?.role?.hierarchyLevel;
        const createdAt = dayjs(log?.createdAt).startOf('day');

        return (
            (!user || fullName.includes(user.toLowerCase())) &&
            (!eventtype || log?.event === eventtype) &&
            (!hierarchylevel || hierarchy === hierarchylevel) &&
            (!date ||
                (createdAt.isSameOrAfter(dayjs(date.from).startOf('day')) &&
                    createdAt.isSameOrBefore(dayjs(date.to).startOf('day')))) &&
            (!search ||
                log?.event?.toLowerCase().includes(search.toLowerCase()) ||
                log?.entity?.toLowerCase().includes(search.toLowerCase()) ||
                log?.entityId?.toString().includes(search.toLowerCase()) ||
                fullName.includes(search.toLowerCase()) ||
                log?.user?.role?.name?.toLowerCase().includes(search.toLowerCase()))
        );
    });
};

export type InvoiceHistoryLog = {
    invoiceDate: string;
    paymentMethod?: string;
    paymentAmount?: string;
    description?: string;
    user?: string;
};

export const invoiceHistoryLogs = (invoice: GetSalesInvoice['invoice'], usersCurrency: string) => {
    if (invoice) {
        const outstandingBalance = convertsCentsToCurrency(invoice?.totalOutstanding ?? 0);
        const user = `${invoice?.user?.firstName} ${invoice?.user?.lastName}`;
        const invoiceDate = formatDate({
            date: new Date(invoice.date),
            format: LONG_DATE_FORMAT,
        });
        const paymentLogs: InvoiceHistoryLog[] = [];
        const historyRecords = (invoice?.paymentAllocations ?? [])
            .filter((paymentAllocation) => {
                return paymentAllocation?.status === PaymentAllocationStatus.UNALLOCATED;
            })
            .map((allocation) => {
                const { paymentMethod } = allocation;
                if (
                    paymentMethod === PAYMENT_METHODS.BANK_TRANSFER.value ||
                    paymentMethod === PAYMENT_METHODS.DIRECT_DEBIT.value
                ) {
                    return {
                        ...allocation,
                        paymentMethod: PAYMENT_METHODS[paymentMethod].label,
                    };
                }
                return {
                    ...allocation,
                    paymentMethod: PAYMENT_METHODS.CARD.label,
                };
            });
        historyRecords.forEach(
            ({
                unallocatedAt,
                allocatedAt,
                amount,
                paymentMethod,
                paymentAllocatedBy,
                paymentUnallocatedBy,
            }) => {
                const deletedBy = `${paymentUnallocatedBy?.firstName} ${paymentUnallocatedBy?.lastName}`;
                const paidBy = `${paymentAllocatedBy?.firstName} ${paymentAllocatedBy?.lastName}`;
                const deletedRecord = {
                    invoiceDate: formatDate({
                        date: new Date(unallocatedAt),
                        format: LONG_DATE_FORMAT,
                    }),
                    description: `Deleted Partial Payment - ${usersCurrency}${convertsCentsToCurrency(
                        amount ?? 0
                    )?.toFixed(2)}`,
                    user: deletedBy,
                };
                const recordedPayment: InvoiceHistoryLog = {
                    invoiceDate: formatDate({
                        date: new Date(allocatedAt),
                        format: LONG_DATE_FORMAT,
                    }),
                    description: `Partial Payment - ${usersCurrency}${convertsCentsToCurrency(
                        amount ?? 0
                    )?.toFixed(
                        2
                    )} / Outstanding Balance ${usersCurrency}${outstandingBalance?.toFixed(
                        2
                    )} - ${paymentMethod}`,
                    user: paidBy,
                };
                paymentLogs.push(recordedPayment);
                paymentLogs.push(deletedRecord);
            }
        );
        return [
            ...[
                {
                    invoiceDate,
                    description: 'Invoice Created',
                    user,
                },
            ],
            ...paymentLogs,
        ];
    }
    return [];
};

export type InvoicePaymentHistoryLog = {
    paymentId: {
        paymentType: string;
        buddyId: number;
        sageId: string;
    };
    paymentDate: string;
    paymentMethod: string;
    paymentAmount: number;
};

export enum PaymentLogType {
    PAYMENT_ALLOCATION = 'PAYMENT_ALLOCATION',
    CREDIT_NOTE = 'CREDIT_NOTE',
}

export const invoicePaymentHistoryLogs = (
    paymentAllocations: PaymentAllocation[],
    creditNotes: CreditNote[]
): InvoicePaymentHistoryLog[] => {
    const allocatedPayments = (paymentAllocations ?? []).filter(({ status }) => {
        return status === PaymentAllocationStatus.ALLOCATED;
    });
    const allocationPayments: InvoicePaymentHistoryLog[] =
        invoicePaymentAllocationLogs(allocatedPayments);

    const creditNotePayments: InvoicePaymentHistoryLog[] = invoiceCreditNoteLogs(creditNotes);
    return [...allocationPayments, ...creditNotePayments];
};

const invoicePaymentAllocationLogs = (paymentAllocations: PaymentAllocation[]) => {
    const allocatedPayments = (paymentAllocations ?? []).filter(({ status }) => {
        return status === PaymentAllocationStatus.ALLOCATED;
    });
    return (allocatedPayments ?? []).map((paymentAllocation) => {
        const { id, allocatedAt, paymentMethod, amount } = paymentAllocation;
        return {
            paymentId: {
                paymentType: PaymentLogType.PAYMENT_ALLOCATION,
                buddyId: id,
                sageId: paymentAllocation.sagePaymentId,
            },
            paymentDate: formatDate({
                date: new Date(allocatedAt),
                format: LONG_DATE_FORMAT,
            }),
            paymentMethod,
            paymentAmount: convertsCentsToCurrency(amount),
        };
    });
};

const invoiceCreditNoteLogs = (creditNotes: CreditNote[]) => {
    return (creditNotes ?? [])
        .filter((creditNote) => {
            return (
                creditNote.status === CreditNoteStatus.CREDITED ||
                creditNote.status === CreditNoteStatus.PARTIALLY_CREDITED
            );
        })
        .map((creditNote) => {
            const { id, sageCreditNoteId, createdAt, totalCreditAmount } = creditNote;
            return {
                paymentId: {
                    paymentType: PaymentLogType.CREDIT_NOTE,
                    buddyId: parseInt(id, 10),
                    sageId: sageCreditNoteId,
                },
                paymentDate: formatDate({
                    date: new Date(createdAt),
                    format: LONG_DATE_FORMAT,
                }),
                paymentMethod: 'Credit Note',
                paymentAmount: convertsCentsToCurrency(totalCreditAmount),
            };
        });
};
